import { useCallback } from "react";
import { toast } from "react-toastify";
import { isProdMode } from "../config/Config";

export function useNotificationHandling() {
  const handleAxiosError = useCallback((error) => {
    console.error("error", error);
    const code = error?.request?.status || "";
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error_message ||
      error?.message ||
      "Network error occurred.";
    const description = error?.response?.data?.description || "";

    return {
      title: "Network Error",
      message: isProdMode ? message : `${code}: ${message}`,
      description: description,
    };
  }, []);

  const handleDatabaseError = useCallback((error) => {
    console.error("error", error);
    const code = error?.request?.status || "";
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error_message ||
      error?.message ||
      "Unknown database error occurred.";
    const description = error?.response?.data?.description || "";

    // postErrorData({
    //   source: "Database",
    //   name,
    //   message,
    //   code,
    //   description,
    // }).catch((error) => console.error("Error logging error data:", error));

    return {
      title: "Database Error",
      message: isProdMode ? message : `${code}: ${message}`,
      description,
    };
  }, []);

  const handleGeneralError = useCallback(
    (error) => ({
      title: error?.name || "Error",
      message: error?.message || "An unknown error occurred",
      description: error?.description || "",
    }),
    []
  );

  const handleErrorByType = useCallback(
    (error, source) => {
      if (source === "Axios") return handleAxiosError(error);
      if (source === "Database") return handleDatabaseError(error);
      return handleGeneralError(error);
    },
    [handleGeneralError, handleDatabaseError, handleAxiosError]
  );

  const createToast = useCallback(
    ({ title, message, description, type = "blank" }) => {
      const toastContent = (
        <div>
          <h6 className="Toastify__toast-title">{title}</h6>
          <p className="Toastify__toast-message">{message}</p>
          {description && (
            <p className="Toastify__toast-description">{description}</p>
          )}
        </div>
      );

      switch (type) {
        case "success":
          toast.success(toastContent, { autoClose: 3000 });
          break;
        case "warning":
          toast.warn(toastContent, { autoClose: 5000 });
          break;
        case "error":
          toast.error(toastContent, { autoClose: false });
          break;
        default:
          toast(toastContent);
      }
    },
    []
  );

  const handleSuccessNotification = useCallback(
    (title, message, description) => {
      createToast({ title, message, description, type: "success" });
    },
    [createToast]
  );

  const handleWarningNotification = useCallback(
    (title, message, description) => {
      createToast({ title, message, description, type: "warning" });
    },
    [createToast]
  );

  const handleInfoNotification = useCallback(
    (title, message, description) => {
      createToast({ title, message, description });
    },
    [createToast]
  );

  const handleErrorNotification = useCallback(
    (error) => {
      let source = "Unknown";
      if (error.code === "ERR_BAD_REQUEST" && error.response?.data?.name)
        source = "Database";
      else if (error.name === "AxiosError") source = "Axios";

      const { title, message, description } = handleErrorByType(error, source);
      createToast({ title, message, description, type: "error" });
    },
    [createToast, handleErrorByType]
  );

  return {
    handleErrorNotification,
    handleSuccessNotification,
    handleInfoNotification,
    handleWarningNotification,
  };
}
